import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  ListItem,
  Paper,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReactComponent as HuggingFace } from 'assets/images/ic_hugging_face.svg';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { DiscoverAIService } from 'services/DiscoverAIService';
import {
  AppFormikPasswordField,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { Toast, Utils } from 'shared/helpers';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImportHistoryCard from './ImportHistoryCard';
import LaunchIcon from '@mui/icons-material/Launch';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

export interface ICredentials {
  _id: string;
  username: string;
  accessToken: string;
  connectiontype: string;
  isActive: boolean;
  createdBy: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const ActiveHuggingFaceCard = (): React.ReactElement => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [credentials, setCredentials] = useState<ICredentials[]>([]);
  const [deleteRecord, setDeleteRecord] = useState<ICredentials>();
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [huggingModels, setHuggingModels] = useState<
    {
      id: string;
      author: string;
      name: string;
      downloads: number;
      url: string;
      imported: boolean;
    }[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [importingIndex, setImportingIndex] = useState<number | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [importHistory, setImportHistory] = useState<
    {
      aiSystemID: string;
      importedDate: string;
      modelName: string;
      status: string;
    }[]
  >([]);
  const [isImportHistory, setIsImportHistory] = useState(false);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  // API Calls
  const getConnections = (): void => {
    setIsLoading(true);
    DiscoverAIService.getConnections('hugging')
      .then((response: any) => {
        const { data } = response.data;
        setCredentials(data);
        if (data && data.length > 0) {
          setSelectedConnectionId(data[0]._id);
        }
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => setIsLoading(false));
  };

  const getImportHistory = (): void => {
    setIsImportHistory(true);
    DiscoverAIService.getImportHistory(selectedConnectionId)
      .then((response: any) => {
        const { data } = response.data;
        setImportHistory(data);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => setIsImportHistory(false));
  };

  const getHuggingModels = (search: string): void => {
    setIsSearching(true);
    DiscoverAIService.getHuggingModels(search, selectedConnectionId)
      .then((response: any) => {
        const { data } = response.data;
        setHuggingModels(data);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => setIsSearching(false));
  };
  const deleteConnection = (id: string, callback: any): void => {
    DiscoverAIService.deleteConnection(id)
      .then((response: any) => {
        callback(true, response);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
        callback(false, error);
      });
  };
  const createConnection = (formValues: any, callback: any): void => {
    DiscoverAIService.createConnection({ ...formValues })
      .then((response: any) => {
        callback(true, response);
      })
      .catch((error: any) => {
        callback(false);
        Utils.handleErrorResponse(error);
      });
  };

  const handleCloseDelete = (): void => {
    setDeleteRecord(undefined);
  };

  const handleDeleteRecord = (): void => {
    deleteConnection(`${deleteRecord?._id}`, (status: boolean) => {
      handleCloseDelete();
      if (status) {
        getConnections();
        Toast.showSuccessMessage('Connection successfully deleted');
      }
    });
  };

  const handleImportHuggingFace = (model: any, index: number): void => {
    setImportingIndex(index);
    const payload = {
      modelName: model.id,
      url: model.url,
      connectionId: selectedConnectionId,
    };
    DiscoverAIService.importHuggingFace(payload)
      .then((response: any) => {
        Toast.showSuccessMessage(response?.data?.msg);

        setHuggingModels((prev) => {
          const updated = prev.map((m) =>
            m.id === model.id ? { ...m, imported: true } : m
          );

          return updated;
        });

        setImportingIndex(null);
      })
      .catch((error: any) => {
        console.log('ERROR RESPONSE', error);
        Utils.handleErrorResponse(error);
        setImportingIndex(null);
      });
  };

  const actionOnFormSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    if (formik) {
      createConnection({ ...formValues }, (status: boolean) => {
        formik.setSubmitting(false);
        setShowAddForm(false);
        if (status) {
          getConnections();
          Toast.showSuccessMessage('Connection successfully added');
        }
      });
    }
  };

  // Hooks
  useEffect(() => {
    getConnections();
  }, []);

  useEffect(() => {
    if (selectedConnectionId) getImportHistory();
  }, [selectedConnectionId]);

  // UI Elements
  return (
    <Card variant="outlined">
      <CardHeader
        avatar={
          <SvgIcon
            component={HuggingFace}
            inheritViewBox
            className="view-icon"
            fontSize="inherit"
            htmlColor="#76A81F"
          />
        }
        title="Hugging Face"
        subheader="Connect to Hugging Face to search and import AI models into your inventory"
      />

      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <>
          {showAddForm && (
            <CardContent>
              <Box className="app-box-border px-5">
                <Typography className="f18 fw-5">
                  New Hugging Face Connection
                </Typography>
                <Typography className="text-gray mb-5">
                  Add a new Hugging Face connection using an access token
                </Typography>

                <Formik
                  enableReinitialize
                  initialValues={{
                    username: '',
                    accessToken: '',
                    connectiontype: 'hugging',
                  }}
                  validateOnMount
                  onSubmit={(formValues, formik) => {
                    actionOnFormSubmit(formValues, formik);
                  }}
                >
                  {({ handleSubmit, isSubmitting }) => (
                    <form autoComplete="off">
                      <AppFormikTextField
                        outerLabel
                        label="Connection Name"
                        name="username"
                        placeholder="My Hugging Face Account"
                      />

                      <AppFormikPasswordField
                        outerLabel
                        label="Hugging Face Access Token"
                        name="accessToken"
                        placeholder="hf_xxxxxxxxxxxxxxx"
                      />
                      <Typography variant="body1" className="text-gray">
                        Token should have read access to models
                      </Typography>

                      <Stack
                        justifyContent="flex-end"
                        direction="row"
                        spacing={5}
                      >
                        <AppPrimaryButtonOutlined
                          className="btn-small"
                          title="Cancel"
                          onClick={() => {
                            setShowAddForm(false);
                          }}
                        />
                        <AppPrimaryButton
                          className="btn-small"
                          title="Create Connection"
                          isLoading={isSubmitting}
                          loadingSize={22}
                          loadingText="CREATING"
                          disabled={isSubmitting}
                          onClick={handleSubmit}
                        />
                      </Stack>
                    </form>
                  )}
                </Formik>
              </Box>
            </CardContent>
          )}

          {credentials.length > 0 &&
            !showAddForm &&
            orgACLConfig.aiInventory?.includes(
              'aiInventory.discoveryAI.huggingFace.addConnection'
            ) && (
              <Stack direction="row" justifyContent="flex-end">
                <AppPrimaryButton
                  className="btn-small mr-5"
                  title="Add Hugging Face Connection"
                  startIcon={
                    <SvgIcon
                      component={Plus}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  }
                  onClick={() => setShowAddForm(true)}
                />
              </Stack>
            )}
          <CardContent>
            {credentials.map((credential) => (
              <ListItem
                key={credential._id}
                className="app-box-border mt-4 static-box"
                onClick={() => setSelectedConnectionId(credential._id)}
                sx={{
                  width: '100%',
                  border:
                    selectedConnectionId === credential._id
                      ? '1px solid #d1f27a'
                      : 'none',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Box>
                    <Stack
                      className="text-primary mb-2"
                      alignItems="center"
                      direction="row"
                      spacing={2}
                    >
                      <SvgIcon
                        component={HuggingFace}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="#76A81F"
                      />
                      <Typography className="f18 fw-6">
                        {credential.username}
                      </Typography>
                    </Stack>
                    <Typography variant="body1" className="text-gray mb-2">
                      Created {Utils.formatDateTime(credential.createdAt)}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => {
                      setDeleteRecord(credential);
                    }}
                    title="Delete"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              </ListItem>
            ))}

            <Box className="app-box-border mt-5">
              <Typography className="f16 fw-5">
                Search Hugging Face Models
              </Typography>
              <Typography className="text-gray">
                Search by model name or author to find AI models
              </Typography>
              <Stack className="my-5" direction="row" spacing={5}>
                <TextField
                  placeholder="Search modes (eg, DeepSeek-OCR or deepseek-ai"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <AppPrimaryButton
                  loadingText="Searching"
                  disabled={searchTerm.trim().length === 0 || isSearching}
                  title="Search"
                  onClick={() => getHuggingModels(searchTerm)}
                />
              </Stack>

              {isSearching && (
                <Box className="empty-content">
                  <CircularProgress />
                </Box>
              )}

              {!isSearching && huggingModels.length > 0 && (
                <Box className="table-system-record">
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table
                      stickyHeader
                      sx={{ minWidth: 650 }}
                      aria-label="huggingface models table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Model Name</TableCell>
                          <TableCell align="left">Author</TableCell>
                          <TableCell align="left">Downloads</TableCell>
                          <TableCell align="left">URL</TableCell>
                          <TableCell align="left">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {huggingModels.map((model, idx) => (
                          <TableRow
                            key={model.name || idx}
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                              >
                                <SvgIcon
                                  component={HuggingFace}
                                  inheritViewBox
                                  className="view-icon"
                                  fontSize="inherit"
                                  htmlColor="#76A81F"
                                />
                                <a
                                  href={model.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Typography className="f16 fw-5 text-primary">
                                    {model.name}
                                  </Typography>
                                </a>
                              </Stack>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {model.author}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {model.downloads}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Button
                                endIcon={<LaunchIcon />}
                                className="btn-small"
                                variant="text"
                                onClick={() => {
                                  window.open(model.url, `${model.id}`);
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                            <TableCell align="left">
                              {model.imported ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <CheckCircleIcon
                                    sx={{ fontSize: 18, color: '#8bbb37' }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      color: '#8bbb37',
                                    }}
                                  >
                                    Imported
                                  </Typography>
                                </Stack>
                              ) : (
                                <AppPrimaryButton
                                  className="btn-extra-small"
                                  title={
                                    importingIndex === idx
                                      ? 'Importing...'
                                      : 'Import'
                                  }
                                  startIcon={<DownloadIcon />}
                                  onClick={() =>
                                    handleImportHuggingFace(model, idx)
                                  }
                                  disabled={importingIndex === idx}
                                  isLoading={importingIndex === idx}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {!isSearching &&
                searchTerm.trim().length > 0 &&
                huggingModels.length === 0 && (
                  <Box className="empty-content">
                    <Typography className="f16">No model matched</Typography>
                  </Box>
                )}
            </Box>

            <Box className="app-box-border mt-5">
              <Typography className="f16 fw-5 mb-2">Import History</Typography>

              <ImportHistoryCard
                importHistory={importHistory}
                isLoading={isImportHistory}
              />
            </Box>
          </CardContent>

          {!showAddForm && credentials.length === 0 && (
            <CardContent>
              <Stack
                direction="column"
                spacing={4}
                justifyContent="center"
                alignItems="center"
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <SvgIcon
                    component={HuggingFace}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                  <Typography variant="h6">
                    No Hugging Face Connections
                  </Typography>
                </Stack>
                <Typography component="span">
                  Connect to Hugging Face to search and import AI models into
                  your inventory
                </Typography>
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.discoveryAI.huggingFace.addConnection'
                ) && (
                  <AppPrimaryButton
                    title="Add Hugging Face Connection"
                    startIcon={
                      <SvgIcon
                        component={Plus}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="#76A81F"
                      />
                    }
                    onClick={() => setShowAddForm(true)}
                  />
                )}
              </Stack>
            </CardContent>
          )}
        </>
      )}

      <ConfirmDialog
        title="Delete Connection"
        subTitle="Are you sure to delete the connection?"
        showConfirm={deleteRecord}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          handleDeleteRecord();
        }}
        onCancel={handleCloseDelete}
        onCloseDialog={handleCloseDelete}
      />
    </Card>
  );
};

export default ActiveHuggingFaceCard;
